import * as React from 'react'
import {
  Branch,
  Commit,
  isMergeCommit,
  isPlayground,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import {
  ArrowUpIcon,
  CommitIcon,
  DotsHorizontalIcon,
  Pencil1Icon,
  PlusIcon,
  RowSpacingIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserData } from '~/hooks-data/use-user-data'
import { commitMutators } from '@basehub/data/replicache/mutators/repo-space/commit/mutators'
import { useTabsStore } from '../../explore/components/tabs/store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { formatToSpecific } from '~/lib/time/utils'
import { CommentIcon } from '../../comments/assets/comment-icon'
import { EditorModeProvider } from '../../explore/components/editor-mode-provider'
import { EditorStoreProvider } from '../../explore/components/editor-store'
import { CRThread } from './comments'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { BranchIcon, MergeIcon } from '~/components/icons/branch-icon'
import { FullBleedContainer } from '../../_components/full-bleed-container'
import { BranchActionsCTA, BranchLink } from './branch-actions-cta'
import {
  useBranchOrCommitSegment,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useTheme } from 'next-themes'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { CommentForm } from '../../comments/components'
import { useUserStore } from '~/app/_store/user-store'
import { useEditorHeight } from '~/hooks/use-editor-width'
import { getOffsetTop } from '~/lib/dom-utils'
import { useCommentsStore } from '../../comments/provider'
import {
  compareAsc,
  endOfDay,
  isAfter,
  isBefore,
  subMilliseconds,
  addMilliseconds,
  compareDesc,
} from 'date-fns'
import { getId } from '@basehub/utils/id/index'
import { useBranchMergeDiff } from '../_hooks/use-merge-diff'
import { BranchSummary } from './summary'
import { toast } from 'sonner'
import isEqual from 'lodash.isequal'
import { useBranches } from '~/hooks-data/use-branches'
import {
  useIsExternalVisitor,
  useIsGuestInHisBranch,
  useIsGuestInOthersBranch,
} from '~/hooks-data/use-user-role'
import { isPlaygroundUser } from '@basehub/data/replicache/playground/ids'

export const BranchTimeline = ({ branch }: { branch: Branch }) => {
  if (branch.isDefault) {
    return <DefaultBranchTimeline branch={branch} paginate />
  } else {
    return <FeatureBranchTimeline branch={branch} />
  }
}

const DefaultBranchTimeline = ({
  branch,
  paginate = false,
}: {
  branch: Branch
  paginate?: boolean
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const { pageCount, setPageCount, pageSize } = useBranchTimelinePageSize({
    enabled: paginate,
  })
  const totalEventsToShow = (pageSize ?? 0) * pageCount
  const activeBranches = useBranches({
    baseBranchId: branch.id,
    archived: false,
  })

  const wipEventDate = React.useMemo(() => {
    // the end of the current day
    return endOfDay(new Date()).toISOString()
  }, [])

  const [commitCursor, setCommitCursor] = React.useState<string | null>(
    branch.headCommitId
  )
  const [commitCount, setCommitCount] = React.useState(0)
  const [eventsByDateMap, setEventsByDateMap] = React.useState<
    Map<string, EventProps>
  >(() => new Map())
  const [_accordionsByRootComment, setAccordionsByRootComment] = React.useState<
    Map<string, string>
  >(() => new Map())
  const commitsMissing = commitCursor ? totalEventsToShow - commitCount : 0
  const [isGettingCommits, setIsGettingCommits] = React.useState(false)
  const commentsMap = useCommentsStore((s) => s.commentsMap)
  const rootCommentsByDate = useCommentsStore((s) => s.rootsByDate)

  /** Load commits based on the number of commits to load and the cursor */
  React.useEffect(() => {
    if (isGettingCommits || !replicache) return
    let newCursor = commitCursor
    let commitsMissingMut = commitsMissing
    if (commitsMissingMut <= 0 || !newCursor) return

    const newCommitEvents: Extract<EventProps, { type: 'commit' | 'merge' }>[] =
      []

    setIsGettingCommits(true)

    replicache
      .query(async (tx) => {
        async function getCommit(id: string) {
          const commit = await commitMutators.getCommit(tx, id)
          newCursor = commit?.parentCommitId ?? null
          if (!commit) return null
          return commit
        }

        while (commitsMissingMut && commitsMissingMut > 0 && newCursor) {
          const commit = await getCommit(newCursor)
          commitsMissingMut--
          if (!commit) break
          if (eventsByDateMap.has(commit.createdAt)) continue
          if (isMergeCommit(commit)) {
            newCommitEvents.push({
              createdAt: commit.createdAt,
              commit,
              type: 'merge',
              baseBranch: branch,
              messageType: 'commit-message',
            })
          } else {
            newCommitEvents.push({
              createdAt: commit.createdAt,
              commit,
              type: 'commit',
            })
          }
          commitsMissingMut--
        }
      })
      .then(() => {
        if (newCommitEvents.length > 0) {
          setCommitCount((p) => p + newCommitEvents.length)
          setEventsByDateMap((prevState) => {
            const newMap = new Map(prevState)
            for (const event of newCommitEvents) {
              newMap.set(event.createdAt, event)
            }
            return newMap
          })
        }
        setCommitCursor(newCursor)
      })
      .finally(() => {
        setIsGettingCommits(false)
      })
  }, [
    eventsByDateMap,
    commitCursor,
    commitsMissing,
    isGettingCommits,
    replicache,
    branch,
  ])

  // make sure head commit is in the timeline (useful for new commits)
  React.useEffect(() => {
    const headCommit = branch.headCommit
    if (!headCommit) return

    setEventsByDateMap((prevState) => {
      if (prevState.has(headCommit.createdAt)) return prevState
      return new Map([
        [
          headCommit.createdAt,
          {
            type: 'commit',
            commit: headCommit,
            createdAt: headCommit.createdAt,
          },
        ],
        ...Array.from(prevState),
      ])
    })
    setCommitCount((p) => p + 1)
  }, [branch.headCommit])

  // add comments to accordions and wip
  React.useEffect(() => {
    const eventsSorted = Array.from(eventsByDateMap.values()).sort((a, b) =>
      compareDesc(new Date(a.createdAt), new Date(b.createdAt))
    )

    function binSearchCommitAfter(date: string) {
      return eventsSorted.find(
        (event) =>
          event.type === 'commit' &&
          isAfter(new Date(date), new Date(event.createdAt))
      )
    }

    const headCommit = branch.headCommit
    let newWipAccordion: Extract<EventProps, { type: 'wip' }> | undefined
    const newAccordions = new Map<
      string,
      Extract<EventProps, { type: 'accordion' | 'wip' }>
    >()
    const eventsForWipAccordion: EventProps[] = []

    for (const [date, rootId] of rootCommentsByDate) {
      const root = commentsMap.get(rootId)
      invariant(root, 'Root comment not found')
      invariant(root.type === 'root', 'Root comment is not a root comment')
      if (root.branchId !== branch.id) continue
      if (
        !headCommit ||
        isAfter(new Date(date), new Date(headCommit.createdAt))
      ) {
        // goes for wip accordion
        eventsForWipAccordion.push({ type: 'thread', rootId, createdAt: date })
        if (!newWipAccordion) {
          newWipAccordion = {
            createdAt: wipEventDate,
            events: [],
            type: 'wip',
          }
        }
      } else {
        const commit = binSearchCommitAfter(date)
        if (!commit) continue
        const eventDate = addMilliseconds(
          new Date(commit.createdAt),
          1
        ).toISOString()
        if (eventsByDateMap.has(eventDate)) {
          continue
        }
        const existing = newAccordions.get(eventDate)
        if (existing) {
          existing.events.push({ type: 'thread', rootId, createdAt: date })
        } else {
          newAccordions.set(eventDate, {
            createdAt: eventDate,
            events: [{ type: 'thread', rootId, createdAt: date }],
            type: 'accordion',
          })
        }
      }
    }

    let wipChanged = false
    if (!newWipAccordion) {
      newWipAccordion = {
        type: 'wip',
        createdAt: wipEventDate,
        events: [],
      }
    }
    if (newWipAccordion) {
      newWipAccordion.events.push(
        ...[...eventsForWipAccordion].sort((a, b) =>
          compareAsc(new Date(a.createdAt), new Date(b.createdAt))
        )
      )
      const existingWipAccordion = eventsByDateMap.get(
        newWipAccordion.createdAt
      )
      if (existingWipAccordion) {
        // see if it changed
        invariant(
          existingWipAccordion.type === 'wip',
          'expected accordion to be "wip" type'
        )
        const changed =
          existingWipAccordion.events.length !==
            newWipAccordion.events.length ||
          existingWipAccordion.events.some(
            (ev) => !newWipAccordion.events.some((newEv) => isEqual(newEv, ev))
          )
        if (changed) {
          wipChanged = true
        }
      } else {
        wipChanged = true
      }
    }

    if ((newWipAccordion && wipChanged) || newAccordions.size > 0) {
      setEventsByDateMap((prevState) => {
        const newMap = new Map(prevState)

        // clear out old accordions
        setAccordionsByRootComment((prevState) => {
          const newMap = new Map(prevState)
          let changed = false
          newAccordions.forEach((accordion) => {
            accordion.events.forEach((ev) => {
              const existingAccordionKey = newMap.get(ev.createdAt)
              if (existingAccordionKey === accordion.createdAt) return
              changed = true
              if (existingAccordionKey) {
                newMap.delete(existingAccordionKey)
              }
              newMap.set(ev.createdAt, accordion.createdAt)
            })
          })
          if (!changed) return prevState
          return newMap
        })

        // add new accordions
        newAccordions.forEach((accordion, date) => {
          newMap.set(date, {
            ...accordion,
            events: accordion.events.sort((a, b) =>
              compareAsc(new Date(a.createdAt), new Date(b.createdAt))
            ),
          })
        })

        if (newWipAccordion) {
          // dont forget to delete old wip accordion
          for (const [key, value] of newMap) {
            if (value.type === 'wip') {
              newMap.delete(key)
              break
            }
          }
          newMap.set(newWipAccordion.createdAt, newWipAccordion)
        }

        return newMap
      })
    }
  }, [
    branch.id,
    branch.createdAt,
    branch.headCommit,
    commentsMap,
    eventsByDateMap,
    rootCommentsByDate,
    wipEventDate,
  ])

  // add active branches to the timeline
  React.useEffect(() => {
    if (!activeBranches.data) return
    setEventsByDateMap((prevState) => {
      const newMap = new Map(prevState)

      let changed = false
      activeBranches.data.forEach((branch) => {
        if (newMap.has(branch.createdAt)) return
        changed = true
        newMap.set(branch.createdAt, {
          type: 'branch-create',
          branch,
          createdAt: branch.createdAt,
        })
      })

      if (!changed) return prevState
      return newMap
    })
  }, [activeBranches.data])

  const sortedKeys = React.useMemo(() => {
    return Array.from(eventsByDateMap.keys()).sort((a, b) =>
      compareAsc(new Date(a), new Date(b))
    )
  }, [eventsByDateMap])

  const paginated = React.useMemo(() => {
    return sortedKeys.slice(0 - totalEventsToShow)
  }, [sortedKeys, totalEventsToShow])

  const everythingShown = totalEventsToShow >= eventsByDateMap.size

  return (
    <div className="flex flex-col pb-[80px]">
      {!everythingShown ? (
        <EventLayout
          accentColor="open"
          icon={<ArrowUpIcon />}
          className="[&_div[data-icon]]:pointer-events-none [&_div[data-icon]]:z-10"
        >
          <Button
            unstyled
            focusStyled
            className="text-sm h-8 font-medium z-0 w-[calc(100%+40px)] cursor-default -translate-x-10 text-left text-dark-muted hover:text-dark-bright pl-4"
            onClick={() => {
              setPageCount((p) => p + 1)
            }}
          >
            <span className="pl-6 pr-2 h-full inline-flex w-full items-center rounded-md">
              Load older events
            </span>
          </Button>
        </EventLayout>
      ) : (
        <Event
          type="create"
          createdAt={branch.createdAt}
          by={data?.repo.contributors[0] ?? ''}
          repoName={data?.repo.title ?? ''}
        />
      )}

      {paginated.map((date, index) => {
        const event = eventsByDateMap.get(date)
        invariant(event, 'event not found')
        const isLast = index === paginated.length - 1
        if (event.type === 'wip' || event.type === 'accordion') {
          return (
            <EventsAccordion
              key={event.createdAt}
              data={event}
              isLast={isLast}
              branch={branch}
            />
          )
        }

        return <Event key={event.createdAt} {...event} skipBorder={isLast} />
      })}
    </div>
  )
}

const FeatureBranchTimeline = ({ branch }: { branch: Branch }) => {
  const { data: repoReplicacheData } = useRepoReplicache()
  const replicache = repoReplicacheData?.replicache
  const { diffCommitLineage, baseBranch, merges } = useBranchMergeDiff({
    branch,
  })
  const user = useUserStore((s) => s.optimisticUser)
  const commentsMap = useCommentsStore((s) => s.commentsMap)
  const rootCommentsByDate = useCommentsStore((s) => s.rootsByDate)
  const isGuestInOthersBranch = useIsGuestInOthersBranch()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const isExternalVisitor = useIsExternalVisitor()

  const [eventsByDateMap, setEventsByDateMap] = React.useState<
    Map<string, EventProps>
  >(new Map())

  // build accordions using merges
  React.useEffect(() => {
    if (!merges || !baseBranch) return

    setEventsByDateMap((prevState) => {
      const newMap = new Map(prevState)
      let changed = false
      for (const merge of merges) {
        const accordion: Extract<EventProps, { type: 'accordion' }> = {
          type: 'accordion',
          createdAt: subMilliseconds(
            new Date(merge.createdAt),
            1
          ).toISOString(),
          events: [],
        }
        const mergeEvent: Extract<EventProps, { type: 'merge' }> = {
          type: 'merge',
          commit: merge,
          baseBranch,
          messageType: 'auto',
          createdAt: merge.createdAt,
        }
        if (
          prevState.has(accordion.createdAt) &&
          prevState.has(mergeEvent.createdAt)
        ) {
          continue
        }
        changed = true
        newMap.set(accordion.createdAt, accordion)
        newMap.set(mergeEvent.createdAt, mergeEvent)
      }

      if (!changed) return prevState

      // sort map by date
      const sorted = Array.from(newMap.entries()).sort((a, b) =>
        compareAsc(new Date(a[0]), new Date(b[0]))
      )

      return new Map(sorted)
    })
  }, [baseBranch, merges])

  // add comments and commits to accordions
  React.useEffect(() => {
    setEventsByDateMap((prevState) => {
      const eventsSorted = Array.from(prevState.values()).sort((a, b) =>
        compareAsc(new Date(a.createdAt), new Date(b.createdAt))
      )

      function binSearchMergeBefore(date: string) {
        return eventsSorted.find(
          (event) =>
            event.type === 'merge' &&
            isBefore(new Date(date), new Date(event.createdAt))
        )
      }

      const headCommit = branch.headCommit
      const newEventsMap = new Map<string, EventProps>(prevState)
      let changed = false

      // add threads
      for (const [date, rootId] of rootCommentsByDate) {
        const root = commentsMap.get(rootId)
        invariant(root, 'Root comment not found')
        invariant(root.type === 'root', 'Root comment is not a root comment')
        if (root.branchId !== branch.id) continue
        if (
          !headCommit ||
          isAfter(new Date(date), new Date(headCommit.createdAt))
        ) {
          if (newEventsMap.has(date)) continue
          changed = true
          newEventsMap.set(date, { type: 'thread', rootId, createdAt: date })
        } else {
          const merge = binSearchMergeBefore(date)
          if (merge) {
            const accordionDate = subMilliseconds(
              new Date(merge.createdAt),
              1
            ).toISOString()
            const accordion = newEventsMap.get(accordionDate)
            if (accordion) {
              if (newEventsMap.has(date)) {
                changed = true
                newEventsMap.delete(date)
              }
              invariant(accordion.type === 'accordion', 'expected accordion')
              if (
                accordion.events.find(
                  (ev) => ev.type === 'thread' && ev.createdAt === date
                )
              ) {
                continue
              }
              changed = true
              accordion.events.push({
                type: 'thread',
                rootId,
                createdAt: date,
              })
            }
          } else {
            if (newEventsMap.has(date)) continue
            changed = true
            newEventsMap.set(date, { type: 'thread', rootId, createdAt: date })
            continue
          }
        }
      }

      // add commits
      for (const commit of diffCommitLineage ?? []) {
        const merge = binSearchMergeBefore(commit.createdAt)
        if (merge) {
          const accordionDate = subMilliseconds(
            new Date(merge.createdAt),
            1
          ).toISOString()
          const accordion = newEventsMap.get(accordionDate)
          if (accordion) {
            if (newEventsMap.has(commit.createdAt)) {
              changed = true
              newEventsMap.delete(commit.createdAt)
            }
            invariant(accordion.type === 'accordion', 'expected accordion')
            if (
              accordion.events.find(
                (ev) => ev.type === 'commit' && ev.commit.id === commit.id
              )
            ) {
              continue
            }
            changed = true
            accordion.events.push({
              type: 'commit',
              commit,
              createdAt: commit.createdAt,
            })
          }
        } else {
          if (newEventsMap.has(commit.createdAt)) continue
          changed = true
          newEventsMap.set(commit.createdAt, {
            type: 'commit',
            commit,
            createdAt: commit.createdAt,
          })
        }
      }

      if (!changed) return prevState
      return newEventsMap
    })
  }, [
    branch.id,
    branch.createdAt,
    branch.headCommit,
    commentsMap,
    rootCommentsByDate,
    diffCommitLineage,
  ])

  const sortedKeys = React.useMemo(() => {
    return Array.from(eventsByDateMap.keys()).sort((a, b) =>
      compareAsc(new Date(a), new Date(b))
    )
  }, [eventsByDateMap])

  return (
    <>
      <div className="flex flex-col">
        {sortedKeys.map((date, index) => {
          const event = eventsByDateMap.get(date)
          invariant(event, 'event not found')
          const isLast = index === sortedKeys.length - 1
          if (event.type === 'accordion') {
            if (event.events.length === 0) return null
            return (
              <EventsAccordion
                key={index}
                data={{
                  ...event,
                  events: [...event.events].sort((a, b) =>
                    compareAsc(new Date(a.createdAt), new Date(b.createdAt))
                  ),
                }}
                isLast={isLast}
                branch={branch}
              />
            )
          }

          return <Event key={index} {...event} />
        })}
      </div>

      <div
        className={clsx(
          'flex-1 flex justify-end flex-col relative',
          "before:content-[''] before:absolute before:w-px before:h-[calc(100%-112px)] before:bg-dark-border-solid before:left-4 before:bottom-[112px]"
        )}
      >
        {!branch.deletedAt ? (
          <BranchActionsCTA branch={branch} />
        ) : (
          <BranchSummary branch={branch} />
        )}
      </div>

      <div className="relative">
        {(!isExternalVisitor ||
          isGuestInHisBranch ||
          (isPlayground(branch) && isPlaygroundUser(user?.id))) && (
          <CommentForm
            editorKey={
              baseBranch
                ? `timeline-new-comment-draft-${baseBranch?.id}`
                : undefined
            }
            placeholder="Leave a comment..."
            className="px-0"
            editorClassName="!bg-dark-shade"
            withBorder
            onArchive={
              !Boolean(branch.archivedAt) &&
              !branch.isDefault &&
              !isGuestInOthersBranch
                ? async () => {
                    if (!user?.id) {
                      return toast.error(
                        'Could not archive branch, this is a bug in BaseHub.'
                      )
                    }
                    await replicache?.mutate.toggleArchiveBranch({
                      id: branch.id,
                      archivedAt: new Date().toISOString(),
                      archivedBy: user.id,
                      archivedByUsername: user.username ?? '',
                    })
                  }
                : undefined
            }
            size="medium"
            onSubmit={async (state, id) => {
              if (!replicache || !user?.id) return

              const { comment } = await replicache.mutate.addComment({
                comment: {
                  ref: undefined,
                  value: state,
                  authorId: user.id,
                  createdAt: new Date().toISOString(),
                  id: id ?? getId(),
                  replyId: null,
                  reactions: {},
                  type: 'root',
                  status: 'open',
                  blockId: undefined,
                  branchId: branch.id,
                },
                isDefaultBranch: branch.isDefault,
                blockId: null,
                blockParentId: null,
                branchSlug: branch.name,
                repoSlug: repoReplicacheData.repo.slug,
                teamSlug: repoReplicacheData.team.slug,
                authorUsername: user.username!,
                repoName: repoReplicacheData.repo?.title,
              })

              setTimeout(() => {
                document
                  .getElementById('comment-box-' + comment.id)
                  ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  })
              }, 50)
            }}
          />
        )}
      </div>
    </>
  )
}

/* -------------------------------------------------------------------------------------------------
 * EventsAccordion
 * -----------------------------------------------------------------------------------------------*/

type AccordionEvents = Partial<Record<EventProps['type'], number>>

const EventsAccordion = ({
  data,
  isLast,
  branch,
}: {
  data: Extract<EventProps, { type: 'accordion' | 'wip' }>
  isLast: boolean
  branch: Branch
}) => {
  const { data: repoReplicacheData } = useRepoReplicache()
  const replicache = repoReplicacheData?.replicache
  const [isOpen, setIsOpen] = React.useState(false)
  const theme = useTheme().resolvedTheme
  const bgColor = theme === 'dark' ? '121212' : 'FAFAFA'
  const borderColor = theme === 'dark' ? '252525' : 'D9D9D9'
  const variant = data.type === 'accordion' ? 'serrucho' : 'compact'
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const workingDiff = useWorkingDiff()
  const selectedBranchOrCommit = useSelectedBranchOrCommitQuery().data

  const events: AccordionEvents = {}
  data.events.forEach((ev) => {
    if (!events[ev.type]) events[ev.type] = 0

    events[ev.type]!++
  })
  const accordionLabelArray = Object.entries(events).map(
    ([typeOfEvents, numberOfEvents]) => {
      const message = [numberOfEvents, typeOfEvents].join(' ')

      if (numberOfEvents === 1) return message
      return `${message}s`
    }
  )
  const allButLast = accordionLabelArray.slice(0, -1)
  const last = accordionLabelArray[accordionLabelArray.length - 1]
  const accordionLabel =
    allButLast.length > 0 ? allButLast.join(', ') + ' and ' + last : last

  const user = useUserStore((s) => s.optimisticUser)

  if (isOpen) {
    return (
      <>
        {data.events.map((event) => {
          return <Event key={event.createdAt} {...event} />
        })}
        {data.type === 'wip' && (
          <div
            className={clsx(
              'relative',
              "after:absolute after:content-[''] after:left-4 after:-top-11 after:bg-dark-border-solid after:h-14 after:w-px"
            )}
          >
            <CommentForm
              editorKey={
                selectedBranchOrCommit
                  ? `wip-comment-draft-${selectedBranchOrCommit.id}`
                  : undefined
              }
              placeholder="Leave a comment..."
              className="px-0"
              editorClassName="!bg-dark-shade"
              withBorder
              size="medium"
              onSubmit={async (state, id) => {
                if (!user || !replicache) return
                await replicache.mutate.addComment({
                  comment: {
                    ref: undefined,
                    value: state,
                    authorId: user.id,
                    createdAt: new Date().toISOString(),
                    id: id ?? getId(),
                    replyId: null,
                    reactions: {},
                    type: 'root',
                    status: 'open',
                    blockId: undefined,
                    branchId: branch.id,
                  },
                  isDefaultBranch: branch.isDefault,
                  blockId: null,
                  blockParentId: null,
                  branchSlug: branch.name,
                  repoSlug: repoReplicacheData.repo.slug,
                  teamSlug: repoReplicacheData.team.slug,
                  authorUsername: user.username!,
                  repoName: repoReplicacheData.repo?.title,
                })
              }}
            />
          </div>
        )}
      </>
    )
  }
  return (
    <div
      className={clsx(
        'relative -mt-9',
        isLast && '-mb-9',
        !isLast &&
          "after:content-[''] after:w-px after:z-[-2] after:bg-dark-border-solid after:h-16 after:translate-x-4 after:top-14 after:absolute"
      )}
    >
      <FullBleedContainer
        bleedContainerClassName={clsx(
          'relative !py-9 z-[1] group',
          variant === 'serrucho' &&
            "after:content-[''] after:bg-dark-base-dark after:inset-x-0 after:top-1/2 after:-translate-y-1/2 after:h-10 after:absolute after:z-[-1]"
        )}
        style={
          variant === 'serrucho'
            ? {
                backgroundSize: 22,
                backgroundRepeat: 'repeat-x',
                backgroundPosition:
                  'calc(0% + 33px) calc(0% + 26px), 0% calc(100% - 26px)',
                backgroundImage: clsx(
                  // top
                  `url("data:image/svg+xml,%3Csvg width='22' height='6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5.79V6h22v-.419L11 .42 0 5.582v.209Z' fill='%23${bgColor}'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='m11 0 11 5.122V6L11 .838.001 6v-.838l11-5.162Z' fill='%23${borderColor}'/%3E%3C/svg%3E"),
                  url("data:image/svg+xml,%3Csvg width='22' height='6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M22 .21V0H0v.419L11 5.58 22 .418V.21Z' fill='%23${bgColor}'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M11 6 0 .878V0l11 5.162L21.999 0V.84L10.999 6Z' fill='%23${borderColor}'/%3E%3C/svg%3E")`
                  // bottom
                ),
              }
            : {}
        }
      >
        <EventLayout
          icon={
            data.type === 'wip' ? (
              <Pencil1Icon className="size-[18px]" />
            ) : (
              <CommentIcon className="text-dark-dim" />
            )
          }
          align="center"
          isLast
          skipBorder
          accentColor={
            data.type === 'wip'
              ? workingDiff.diff?.changesCount
                ? 'ready'
                : 'neutral'
              : 'transparent'
          }
        >
          <div className="flex items-center justify-between text-sm text-dark-dim">
            <span className="overflow-hidden mr-2 overflow-ellipsis text-dark-muted">
              {data.type === 'wip' && (
                <>
                  <span className="font-medium text-dark-dim">WIP</span>{' '}
                  <ButtonLink
                    unstyled
                    focusStyled
                    className="hover:underline"
                    href={`/${teamSlug}/${repoSlug}/changes/${encodedSegment}`}
                  >
                    {workingDiff.diff?.changesCount ? (
                      <>
                        {workingDiff.diff.changesCount} change
                        {workingDiff.diff.changesCount === 1 ? '' : 's'}
                      </>
                    ) : (
                      'No changes'
                    )}
                  </ButtonLink>
                  {data.events.length ? ',' : ''}&nbsp;
                </>
              )}
              <Button
                unstyled
                focusStyled
                className="hover:underline"
                onClick={() => setIsOpen(true)}
              >
                {accordionLabel}
              </Button>
            </span>

            <Button
              unstyled
              focusStyled
              className="text-dark-faint hover:text-dark-muted cursor-default flex items-center gap-1 font-medium text-xs invisible group-hover:visible"
              onClick={() => setIsOpen(true)}
            >
              <RowSpacingIcon />
              See conversation
            </Button>
          </div>
        </EventLayout>
      </FullBleedContainer>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * EventLayout
 * -----------------------------------------------------------------------------------------------*/

const EVENT_PADDING_BOTTOM = 40

type EventLayoutProps = {
  skipBorder?: boolean
  isLast?: boolean
  icon?: React.ReactNode
  date?: Date | null
  align?: 'start' | 'center'
  children?: React.ReactNode
  className?: string
  accentColor?:
    | 'merged'
    | 'open'
    | 'archived'
    | 'neutral'
    | 'conflict'
    | 'ready'
    | 'transparent'
}

export const EventLayout = ({
  isLast,
  icon,
  date,
  align = 'start',
  children,
  className,
  accentColor = 'open',
  skipBorder = false,
}: EventLayoutProps) => {
  const timeDiff = useTimeDiff(date ?? new Date())

  return (
    <EditorModeProvider value="preview">
      <EditorStoreProvider>
        <div
          className={clsx(
            'w-full flex gap-2 justify-between relative group',
            className
          )}
        >
          {!skipBorder && (
            <div
              data-border
              className={clsx(
                'h-[calc(100%+theme(spacing.4))] absolute top-0 left-4 w-px bg-dark-border-solid'
              )}
            />
          )}

          <div
            style={!isLast ? { paddingBottom: EVENT_PADDING_BOTTOM } : {}}
            className={clsx(
              align === 'start' ? 'items-start' : 'items-center',
              'flex gap-2 w-full max-w-full break-words shrink z-10 grow-0 [word-break:break-word] overflow-hidden'
            )}
          >
            <div
              data-icon
              className={clsx(
                {
                  'bg-dark-shade text-dark-muted border-dark-border-solid':
                    accentColor === 'archived' || accentColor === 'open',
                  'bg-dark-border-solid text-dark-muted border-dark-elevated':
                    accentColor === 'neutral',
                  'border-transparent bg-yellow-diff text-dark-bg':
                    accentColor === 'conflict',
                  'border-transparent bg-dark-control-base text-button-label':
                    accentColor === 'ready',
                  'border-transparent bg-dark-cr-merged text-button-label':
                    accentColor === 'merged',
                  'border-transparent': accentColor === 'transparent',
                },
                'w-8 h-8 flex items-center justify-center relative rounded-full shrink-0 border'
              )}
            >
              {icon}
            </div>
            <div className="flex-grow max-w-full break-words overflow-hidden">
              {children}
            </div>
          </div>

          {date && (
            <SimpleTooltip content={formatToSpecific(date)}>
              <div className="text-xs text-dark-faint shrink-0 whitespace-nowrap cursor-default pt-1">
                {timeDiff}
              </div>
            </SimpleTooltip>
          )}
        </div>
      </EditorStoreProvider>
    </EditorModeProvider>
  )
}

const MergeEvent = ({
  commit,
  baseBranch,
  messageType,
}: Pick<
  Extract<EventProps, { type: 'merge' }>,
  'commit' | 'baseBranch' | 'messageType'
>) => {
  const username = useUserData(commit.authorId).data?.username

  if (messageType === 'commit-message') {
    return (
      <CommitLink
        className="text-xs text-dark-muted font-mono hover:underline leading-[21px] pt-1 inline-block text-pretty"
        commitId={commit.id}
      >
        {commit.message}
      </CommitLink>
    )
  } else if (messageType === 'auto') {
    return (
      <div className="text-xs text-dark-muted font-mono leading-[21px] pt-1">
        <span className="text-dark-bright">{username}</span> merged into{' '}
        <BranchLink className="text-dark-bright" branch={baseBranch} /> at
        commit{' '}
        <CommitLink
          commitId={commit.id}
          className="text-dark-bright hover:underline"
        />
      </div>
    )
  }
}

/* -------------------------------------------------------------------------------------------------
 * Event
 * -----------------------------------------------------------------------------------------------*/

type EventProps = Pick<EventLayoutProps, 'skipBorder'> & {
  createdAt: string
  pb?: 'none' | 'normal' | 'large'
} & (
    | {
        type: 'commit'
        commit: Commit
      }
    | {
        type: 'thread'
        rootId: string
      }
    | {
        type: 'wip'
        events: EventProps[]
      }
    | {
        type: 'accordion'
        events: EventProps[]
      }
    | {
        type: 'archive'
        at: string
        by: string
      }
    | {
        type: 'merge'
        commit: Commit
        baseBranch: Branch
        messageType: 'commit-message' | 'auto'
      }
    | {
        type: 'branch-create'
        branch: Branch
      }
    | {
        type: 'create'
        by: string
        repoName: string
      }
  )

const Event = (props: EventProps) => {
  let eventIcon: React.ReactNode
  let authorId: string = ''
  let content: React.ReactNode
  let date: Date | null = null
  let accentColor: React.ComponentProps<typeof EventLayout>['accentColor'] =
    'open'
  const pb = props.pb ?? 'normal'
  const rootComment = useCommentsStore((s) => {
    if (props.type !== 'thread') return
    return s.commentsMap.get(props.rootId)
  })

  switch (props.type) {
    case 'commit':
      eventIcon = <CommitIcon />
      date = new Date(props.commit.createdAt)
      authorId = props.commit.authorId
      content = (
        <CommitLink
          className="text-xs text-dark-muted font-mono hover:underline leading-[21px] pt-1 inline-block text-pretty"
          commitId={props.commit.id}
        >
          {props.commit.message}
        </CommitLink>
      )
      break
    case 'branch-create':
      eventIcon = <BranchIcon />
      date = new Date(props.createdAt)
      authorId = props.branch.authorId ?? ''
      content = (
        <div className="text-xs text-dark-muted font-mono leading-[21px] pt-1 inline-block text-pretty">
          Created branch{' '}
          <BranchLink branch={props.branch} className="text-dark-dim" />
        </div>
      )
      break
    case 'thread':
      invariant(rootComment, 'rootComment is null')
      invariant(
        rootComment.type === 'root',
        'rootComment is not a root comment'
      )
      eventIcon = <CommentIcon className="text-dark-dim" />
      authorId = rootComment.authorId
      date = null
      content = (
        <CRThread
          root={rootComment}
          key={`${rootComment.id}-${rootComment.lastEditedAt}-${rootComment.replyId}`}
        />
      )
      break
    case 'merge':
      eventIcon = <MergeIcon />
      authorId = props.commit.authorId
      accentColor = 'merged'
      date = new Date(props.commit.createdAt)
      content = <MergeEvent {...props} />
      break
    case 'create':
      eventIcon = <PlusIcon />
      authorId = props.by
      accentColor = 'open'
      date = new Date(props.createdAt)
      content = (
        <RepositoryCreateEvent
          className="text-sm text-dark-muted leading-[21px] pt-1 inline-block text-pretty"
          by={props.by}
          repoName={props.repoName}
        />
      )
      break
    default:
      break
  }

  const { data: userData } = useUserData(authorId)

  // remove this invariant as it's not necesary, in comments i intentionally left it null
  // invariant(date, 'date is null')
  invariant(content, 'content is null')

  return (
    <EventLayout
      date={date}
      accentColor="open"
      isLast={pb === 'none'}
      skipBorder={props.skipBorder}
      icon={
        <>
          <SimpleTooltip
            content={userData?.username}
            disabled={!Boolean(userData?.username)}
          >
            <span className="h-full w-full">
              <BaseHubAvatar
                id={authorId}
                name={userData?.username || authorId}
                size="fill"
              />
            </span>
          </SimpleTooltip>
          <div
            className={clsx(
              'absolute w-6 h-6 rounded-full left-1/2 -translate-x-1/2 top-6 flex items-center justify-center border',
              {
                'bg-dark-shade text-dark-dim border-dark-border-solid':
                  accentColor === 'open',
                'bg-dark-cr-merged text-button-label border-transparent':
                  accentColor === 'merged',
              }
            )}
          >
            {eventIcon}
          </div>
        </>
      }
      className={clsx({
        '[&_div[data-icon]]:mt-3 [&_div[data-border]]:top-3':
          props.type === 'thread',
        '!pb-0': pb === 'none',
        '!pb-14': pb === 'large',
      })}
    >
      <div className="text-sm">{content}</div>
    </EventLayout>
  )
}

const CommitLink = ({
  commitId,
  children,
  className,
}: {
  commitId: string
  children?: React.ReactNode
  className?: string
}) => {
  const addTab = useTabsStore((s) => s.addTab)
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const [isCollapsed, setIsCollapsed] = React.useState(true)

  const textElementRef = React.useRef<HTMLAnchorElement>(null)
  const buttonElementRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!textElementRef.current || !buttonElementRef.current) return
    if (!isCollapsed) {
      buttonElementRef.current.style.display = 'none'
    }

    const checkOverflow = () => {
      if (!textElementRef.current || !buttonElementRef.current) return

      if (
        textElementRef.current.scrollWidth > textElementRef.current.clientWidth
      ) {
        buttonElementRef.current.style.display = 'block'
      } else {
        buttonElementRef.current.style.display = 'none'
      }
    }

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(textElementRef.current as Element)

    return () => {
      observer.disconnect()
    }
  }, [isCollapsed])

  return (
    <div className="inline-flex items-start overflow-hidden group-hover max-w-full">
      <ButtonLink
        focusStyled
        unstyled
        ref={textElementRef}
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}/${commitId}`}
        className={clsx(
          className,
          isCollapsed && '!whitespace-nowrap text-ellipsis overflow-hidden'
        )}
        onClick={() =>
          addTab({ key: `commit:${commitId}`, partiallyOpen: true })
        }
        onDoubleClick={() =>
          addTab({ key: `commit:${commitId}`, partiallyOpen: false })
        }
      >
        {children ?? `#${commitId.substring(0, 7)}`}
      </ButtonLink>

      <SimpleTooltip
        content={(isCollapsed ? 'Show' : 'Hide') + ` full commit message`}
      >
        <Button
          ref={buttonElementRef}
          unstyled
          focusStyled
          className="p-0.5 rounded-sm bg-dark-shade hover:bg-dark-shade-hover cursor-default mr-1 ml-2 mt-1 inline-block"
          onClick={() => setIsCollapsed((prev) => !prev)}
        >
          <DotsHorizontalIcon />
        </Button>
      </SimpleTooltip>
    </div>
  )
}

const RepositoryCreateEvent = ({
  className,
  by,
  repoName,
}: {
  className?: string
  by: string
  repoName: string
}) => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const username = useUserData(by).data?.username

  return (
    <div className={className}>
      <ButtonLink
        focusStyled
        unstyled
        href={`/${teamSlug}/${repoSlug}/${username}`}
        className="text-dark-bright hover:underline font-medium"
      >
        {username}
      </ButtonLink>
      &nbsp;created the repository&nbsp;
      <ButtonLink
        focusStyled
        unstyled
        href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}`}
        className="text-dark-bright hover:underline font-medium"
      >
        {repoName}
      </ButtonLink>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                    Utils                                   */
/* -------------------------------------------------------------------------- */

const useBranchTimelinePageSize = (opts?: { enabled: boolean }) => {
  const enabled = opts?.enabled ?? true
  const editorHeight = useEditorHeight()

  const [pageSize, setPageSize] = React.useState<number>()
  const [pageCount, setPageCount] = React.useState(1)

  const resolvePageSize = React.useCallback(() => {
    if (!enabled) return Infinity

    const timelineElement =
      document.querySelector<HTMLDivElement>('#branch-timeline')
    if (!timelineElement) return null

    const offsetTop = getOffsetTop(timelineElement)
    if (!editorHeight) return null

    const PADDING_BOTTOM = 80
    const timelineHeight = editorHeight - PADDING_BOTTOM - offsetTop
    const eventHeight = 72

    const pageSize = Math.max(Math.floor(timelineHeight / eventHeight), 2)

    return pageSize
  }, [enabled, editorHeight])

  React.useEffect(() => {
    const calculatedPageSize = resolvePageSize()
    if (calculatedPageSize === null) return
    if (!pageSize) {
      setPageSize(calculatedPageSize)
    }
  }, [pageSize, resolvePageSize])

  return { pageSize, pageCount, setPageCount }
}
